import { useQuery } from '@apollo/client/react'
import { RoleEnum } from '@zeus/index'
import { KeyRound, Mail, MonitorPlay, Pencil } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import Spinner from '~/components/Spinner'
import { Button } from '~/components/ui/button'
import { GQL_LEARNER_PROFILE } from '~/gql/users/learner-profile'
import { isNotFoundError } from '~/helpers/api/not-found'
import { useAuth } from '~/helpers/store/auth'
import { canImpersonate } from '~/helpers/users/can-impersonate'
import UserImpersonateModal from '~/pages/backoffice/admin/user/components/UserImpersonateModal'
import UserModal from '~/pages/backoffice/admin/user/components/UserModal'
import { useCredentialsFlow } from '~/pages/backoffice/admin/user/credentials/use-credentials-flow'
import LearnerActivity from './LearnerActivity'
import LearnerLogins from './LearnerLogins'
import LearnerPositioning from './LearnerPositioning'
import LearnerProfileView from './LearnerProfileView'

/** Fiche d'un apprenant (#242), ouverte depuis la liste des utilisateurs. */
function LearnerProfilePage() {
  const { t } = useTranslation()
  const { userId = '' } = useParams()
  const { payload } = useAuth()
  const [editing, setEditing] = useState(false)
  const [impersonating, setImpersonating] = useState(false)
  const credentials = useCredentialsFlow()

  const { data, loading, error, refetch } = useQuery(GQL_LEARNER_PROFILE, {
    variables: { userId },
    fetchPolicy: 'cache-and-network',
  })
  const profile = data?.learnerProfile

  if (loading && !profile) {
    return (
      <div className="flex justify-center p-16">
        <Spinner className="size-6 text-muted-foreground" />
      </div>
    )
  }

  if (!profile) {
    // « Introuvable » seulement quand l'API le dit : une panne n'est pas un compte supprimé.
    const notFound = !error || isNotFoundError(error)
    return (
      <div role={notFound ? undefined : 'alert'} className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-border p-12 text-center">
        <p className="font-medium">{t(notFound ? 'profile.notFound' : 'profile.loadError')}</p>
        {notFound
          ? (
              <Button asChild variant="outline">
                <Link to="/back-office/users">{t('profile.back')}</Link>
              </Button>
            )
          : <Button variant="outline" onClick={() => void refetch()}>{t('profile.retry')}</Button>}
      </div>
    )
  }

  // Les mutations de compte sont `@Roles(Admin)` côté API, comme dans la liste.
  const isAdmin = payload?.role === RoleEnum.Admin
  const mayImpersonate = canImpersonate({ role: payload?.role, id: payload?.id }, { id: profile.id, roleName: profile.roleName })

  return (
    <>
      <Link to="/back-office/users" className="w-fit text-sm font-medium text-primary-text underline underline-offset-4">
        {t('profile.back')}
      </Link>
      <LearnerProfileView
        profile={profile}
        headerActions={(
          <>
            {profile.email && (
              <Button asChild variant="outline">
                <a href={`mailto:${profile.email}`}>
                  <Mail className="mr-2 size-4" aria-hidden />
                  {t('profile.email')}
                </a>
              </Button>
            )}
            {isAdmin && !profile.email && profile.username && (
              <Button variant="outline" onClick={() => profile.username && credentials.startReset({ id: profile.id, username: profile.username, firstName: profile.firstName, lastName: profile.lastName })}>
                <KeyRound className="mr-2 size-4" aria-hidden />
                {t('credentials.reset.action')}
              </Button>
            )}
            {mayImpersonate && (
              <Button variant="outline" onClick={() => setImpersonating(true)}>
                <MonitorPlay className="mr-2 size-4" aria-hidden />
                {t('common.impersonate')}
              </Button>
            )}
          </>
        )}
        infoAction={isAdmin && (
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
            <Pencil className="mr-2 size-4" aria-hidden />
            {t('common.update')}
          </Button>
        )}
      >
        <LearnerActivity userId={profile.id} />
        <LearnerPositioning userId={profile.id} />
        <LearnerLogins userId={profile.id} />
      </LearnerProfileView>
      {isAdmin && editing && (
        <UserModal
          open
          onCancel={() => setEditing(false)}
          onSuccess={() => {
            setEditing(false)
            void refetch()
          }}
          defaultValues={{ id: profile.id, email: profile.email ?? undefined, username: profile.username, company: profile.company, department: profile.department, firstName: profile.firstName, lastName: profile.lastName, roleId: profile.roleId }}
        />
      )}
      {credentials.dialogs}
      {mayImpersonate && impersonating && (
        <UserImpersonateModal open onCancel={() => setImpersonating(false)} user={profile} />
      )}
    </>
  )
}

export default LearnerProfilePage
